import '@react-sigma/core/lib/style.css';

import { Button } from '@praetorian-chariot/ui';
import { useLoadGraph, useSigma } from '@react-sigma/core';
import { ClockAlert } from 'lucide-react';
import React, { FC, useCallback, useEffect, useMemo } from 'react';

// Import Node programs
// Add Asset interface near the top of the file with other interfaces
import { useTheme } from '@/app/ThemeContext';
import { Loader } from '@/components/Loader';
import { ClusterCanvas } from '@/components/nodeGraph/ClusterCanvas';
import { GRAPH_DEFAULTS } from '@/components/nodeGraph/config/graphConfig';
import { ClusterWeightControls } from '@/components/nodeGraph/controls/ClusterWeightControls';
import { useGraphStateContext } from '@/components/nodeGraph/core';
// Import Loader
import {
  useClusterManagement,
  useGraphEventHandlers,
  useGraphLayout,
  useGraphRenderLoop,
} from '@/components/nodeGraph/hooks';
import { useGraphData } from '@/components/nodeGraph/hooks/data/useGraphData';
import { useGraphAttributeVisibility } from '@/components/nodeGraph/hooks/useGraphAttributeVisibility';
import { useImageCache } from '@/components/nodeGraph/hooks/useImageCache';
import { NodeDataTable } from '@/components/nodeGraph/NodeDataTable';
import { DefaultVisibleAttributesForCluster } from '@/components/nodeGraph/utils/constants';
import { buildGraphFromConsolidatedStructure } from '@/components/nodeGraph/utils/graphConstruction';
import { GraphEntityType } from '@/components/nodeGraph/utils/types';
import { Filters } from '@/hooks/useAssets';
import { useStableValue } from '@/hooks/useStableValue';
import { useGlobalState } from '@/state/global.state';
import {
  Asset,
  AssetStatus,
  Attribute,
  ResourceType,
  Risk,
  Seed,
  Technology,
} from '@/types';

interface GraphLoaderProps {
  clusterByEntity?: GraphEntityType;
  setClusterByEntity?: (entityType: GraphEntityType) => void;
  filters?: Filters;
  setFilters?: (filters: Filters) => void;
}

/**
 * GraphLoader - Core Graph Data Management Component
 *
 * This is the central orchestrator for the NodeGraph visualization system. It manages
 * the entire graph lifecycle from data loading to interactive state management.
 *
 * Responsibilities:
 * - Asset data loading and pagination (1000 assets per page)
 * - Graph construction with asset and attribute nodes
 * - Force-directed layout management with FA2Layout
 * - Interactive state management (selection, hover, clusters)
 * - Performance optimization with rendering loops
 * - Child component coordination (ClusterCanvas, AttributeWeightControls, NodeDataTable)
 *
 * Key Features:
 * - Dynamic graph construction from asset data
 * - Real-time layout calculations with adaptive parameters
 * - Cluster detection and management
 * - Node selection and interaction handling
 * - Attribute visibility controls
 * - Memory-efficient image caching
 * - Responsive rendering at 15 FPS
 *
 * State Management:
 * - Graph instance and Sigma integration
 * - Selected nodes and assets
 * - Cluster data and selection
 * - Attribute weights and visibility
 * - Layout state and performance optimization
 */
export const GraphLoader: FC<GraphLoaderProps> = ({
  clusterByEntity: propClusterByEntity,
  setClusterByEntity: _propSetClusterByEntity,
  filters,
  setFilters,
}) => {
  const sigma = useSigma();
  const { currentTheme } = useTheme();
  const { drawerState } = useGlobalState();
  const imageCache = useImageCache();

  // Use GraphStateContext for all UI state management
  const { selection, entity, visibility, config, instance } =
    useGraphStateContext();

  // Destructure selection state
  const { nodes: selectedNodes, cluster: selectedClusterValue } = selection;

  // Destructure entity state
  const { type: localClusterByEntity } = entity;

  // Destructure visibility state
  const { state: visibleState } = visibility;

  // Destructure config state
  const { maxNodes, showOutdatedWarning, setShowOutdatedWarning } = config;

  // Destructure instance state
  const {
    graphRef,
    setGraphRef,
    isInitialLoad,
    setIsInitialLoad,
    existingPositions,
  } = instance;

  // Use prop values if provided, otherwise use local state
  const clusterByEntity = propClusterByEntity ?? localClusterByEntity;

  // Get weights from GraphStateContext
  const { clustering } = useGraphStateContext();
  const currentWeights = clustering.weights;

  // Get active clustering attribute for proportional sampling
  const activeClusterAttribute = useMemo(() => {
    const activeWeight = currentWeights.find(w => w.weight === 20);
    return activeWeight?.name || null;
  }, [currentWeights]);

  const {
    consolidatedGraph,
    assets,
    risks,
    isLoading,
    technologies,
    assetFilters,
    setAssetFilters,
    seeds,
    isLoadingRelatedData,
  } = useGraphData(
    clusterByEntity,
    filters,
    maxNodes,
    activeClusterAttribute, // Enable proportional sampling
    undefined // Let hook determine totalAssets from statistics
  );

  const getSerializedEntities = useCallback(
    (entities: Asset[] | Risk[] | Technology[] | Attribute[] | Seed[]) => {
      return entities?.map(entity => entity.key).join(',') || '';
    },
    []
  );

  // Helper to check if asset has valid AS data (for filtering)
  const hasValidASData = useCallback((asset: Asset): boolean => {
    // Check ASName
    const asNameValue = asset.asname || asset.metadata?.asname;
    if (asNameValue) {
      const trimmed = asNameValue.trim().toLowerCase();
      if (
        trimmed === '' ||
        trimmed === 'unknown' ||
        trimmed === 'undefined' ||
        trimmed === 'null' ||
        trimmed === 'none'
      ) {
        return false;
      }
    }

    // Check ASNumber - 0 means non-routable
    const asNumberValue = asset.asnumber || asset.metadata?.asnumber;
    if (asNumberValue !== undefined && asNumberValue !== null) {
      // Convert to number for comparison
      const asNumberNum =
        typeof asNumberValue === 'string'
          ? parseInt(asNumberValue, 10)
          : asNumberValue;
      if (asNumberNum === 0) {
        return false;
      }
    }

    // If we have neither ASName nor ASNumber, it's non-routable
    if (!asNameValue && !asNumberValue) {
      return false;
    }

    return true;
  }, []);

  // Only filter non-routable assets when clustering by AS fields
  const routableAssets = useMemo(() => {
    // Check if we're clustering by AS attributes
    const isASClustering =
      clusterByEntity === ResourceType.Asset &&
      currentWeights.some(
        w =>
          w.weight === 20 && ['asname', 'asnumber', 'asrange'].includes(w.name)
      );

    // Only filter if we're clustering by AS fields
    if (isASClustering) {
      return assets.filter(asset => hasValidASData(asset));
    }

    // For origins and other clustering, show all assets
    return assets;
  }, [assets, hasValidASData, clusterByEntity, currentWeights]);

  // Use enhanced cluster management hook with entity-specific logic
  const { clusters } = useClusterManagement({
    graphRef,
    currentWeights,
    clusterByEntity,
    assets: routableAssets,
    serialisedAssets: getSerializedEntities(routableAssets),
    risks,
    serialisedRisks: getSerializedEntities(risks),
    technologies,
    serialisedTechnologies: getSerializedEntities(technologies),
    seeds,
    serialisedSeeds: getSerializedEntities(seeds),
  });

  // Use graph layout hook
  const { createLayout, startLayout, resetGraphLayout } =
    useGraphLayout(isInitialLoad);

  // Use graph render loop hook
  useGraphRenderLoop(sigma, graphRef);

  // Use attribute visibility hook to control attribute node visibility
  // (activeClusterAttribute calculated above before useGraphData call)
  useGraphAttributeVisibility({
    visibilityState: visibleState,
    selectedNodes,
    sigma,
    graphRef,
    activeClusterAttribute, // Pass to auto-hide non-clustering attributes
  });

  // Viewport culling temporarily disabled - was interfering with layout
  // const shouldEnableCulling = graphRef ? graphRef.order > 500 : false;
  // const { stats: cullingStats } = useViewportCulling({
  //   sigma,
  //   graph: graphRef,
  //   enabled: shouldEnableCulling,
  //   bufferFactor: 1.5,
  // });

  const loadGraph = useLoadGraph();

  // Memoize consolidated graph key for stable dependency
  const consolidatedGraphKey = useMemo(
    () => Object.keys(consolidatedGraph).sort().join(','),
    [consolidatedGraph]
  );

  // Memoize current weights serialization for stable dependency
  const currentWeightsKey = useMemo(
    () => currentWeights.map(w => `${w.name}:${w.weight}`).join(','),
    [currentWeights]
  );

  // Clear graph when loading starts
  useEffect(() => {
    if (isLoading && graphRef) {
      // Clear the existing graph to prevent showing stale data
      graphRef.clear();
      sigma.refresh();
    }
  }, [isLoading, graphRef, sigma]);

  // Progressive graph construction - start with assets, add related data as it loads
  useEffect(() => {
    // Skip if still loading the initial data
    if (isLoading) {
      return;
    }

    // Allow building graph even with no assets (empty state will be shown)
    if (
      isLoadingRelatedData ||
      !consolidatedGraph ||
      Object.keys(consolidatedGraph).length === 0
    ) {
      return;
    }

    // Defer graph construction by one frame to allow UI updates to render first
    const timeoutId = setTimeout(() => {
      if (graphRef) {
        graphRef.forEachNode(nodeId => {
          existingPositions.set(nodeId, {
            x: graphRef.getNodeAttribute(nodeId, 'x'),
            y: graphRef.getNodeAttribute(nodeId, 'y'),
          });
        });
      }

      // Always filter out non-routable assets from the graph
      let filteredConsolidatedGraph = Object.entries(consolidatedGraph).reduce(
        (acc, [key, node]) => {
          // Only filter Asset nodes
          if (node.type === ResourceType.Asset) {
            const asset = node.data as Asset;

            // Skip assets without valid AS information (non-routable)
            if (!hasValidASData(asset)) {
              return acc; // Don't add this node
            }
          }

          // Keep all other node types and assets with AS info
          acc[key] = node;
          return acc;
        },
        {} as typeof consolidatedGraph
      );

      const graph = buildGraphFromConsolidatedStructure({
        consolidatedGraph: filteredConsolidatedGraph,
        currentTheme,
        imageCache,
        existingPositions,
        attributeWeights:
          currentWeights.length > 0
            ? currentWeights
            : [{ name: 'default', label: 'Default', weight: 1 }],
        visibleState,
      });

      // Load the graph with static positioning (no pre-positioning algorithm)
      loadGraph(graph);
      setGraphRef(graph);

      // Set initial camera zoom from config
      if (GRAPH_DEFAULTS.initialCameraRatio !== undefined) {
        sigma
          .getCamera()
          .setState({ ratio: GRAPH_DEFAULTS.initialCameraRatio });
      }

      sigma.refresh();
      const layout = createLayout(graph);
      startLayout(layout, graph);
      setIsInitialLoad(false);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      if (graphRef) {
        resetGraphLayout(graphRef);
      }
    };
  }, [
    consolidatedGraphKey, // ✅ Stable key - use this instead of consolidatedGraph
    isLoadingRelatedData,
    currentTheme,
    isInitialLoad,
    currentWeightsKey, // ✅ Stable key - use this instead of currentWeights
    clusterByEntity,
    isLoading,
    // ❌ REMOVED: Callback functions that are stable useCallback references
    // resetGraphLayout, createLayout, startLayout, startRendering, stopRendering
    // These are intentionally excluded to prevent infinite loops while being used in effect body
    loadGraph,
    setGraphRef,
    sigma,
    setIsInitialLoad,
    existingPositions,
    imageCache,
    visibleState,
    hasValidASData,
    // ❌ REMOVED: consolidatedGraph - causes infinite loop (using consolidatedGraphKey instead)
    // ❌ REMOVED: currentWeights - causes infinite loop (using currentWeightsKey instead)
  ]);

  // Separate effect to update edge weights without rebuilding the graph
  useEffect(() => {
    if (!graphRef || !currentWeights.length) return;

    const activeWeight = currentWeights.find(w => w.weight === 20);
    const activeAttribute = activeWeight?.name;
    const isDomainClustering = activeAttribute === 'domain';

    // Update existing edge weights based on current weights
    graphRef.forEachEdge((edgeId, _attributes, _source, target) => {
      // Update attribute edges based on weight config
      if (target.startsWith('#attribute#')) {
        const label = graphRef.getNodeAttribute(target, 'label');
        const field = label.split(':')[0]; // Extract field name from label

        // Find matching weight configuration
        const weightConfig = currentWeights.find(w => w.name === field);
        if (weightConfig) {
          graphRef.setEdgeAttribute(edgeId, 'weight', weightConfig.weight);
        }
      }
      // For non-attribute edges (seeds, risks, tech, webpages)
      else {
        if (isDomainClustering) {
          // For domain clustering, drastically reduce relationship edge weights to minimize spread
          // This keeps same-domain assets tightly clustered despite having different relationships
          graphRef.setEdgeAttribute(edgeId, 'weight', 0.1);
        } else {
          // For other clustering modes, restore normal edge weight
          graphRef.setEdgeAttribute(edgeId, 'weight', 1);
        }
      }
    });

    sigma.refresh();
  }, [currentWeights, graphRef, sigma]);

  // Use graph event handlers hook
  useGraphEventHandlers({
    currentTheme,
    selectedNodes,
    imageCache,
    assets, // Use combined assets
    drawerState,
    sigma,
    risks,
    seeds,
    technologies,
  });

  // Stabilize assetFilters to prevent infinite loops from object reference changes
  const stableAssetFilters = useStableValue(assetFilters);

  // Check filters on mount and when filters change to show/hide warning
  // Only show the warning for Assets, not for Seeds or other entity types
  useEffect(() => {
    // Don't show warning if we're not clustering by Assets
    if (clusterByEntity !== ResourceType.Asset) {
      setShowOutdatedWarning(false);
      return;
    }

    const currentStatusFilter =
      stableAssetFilters?.advancedFilters?.status || [];
    const isShowingFresh = currentStatusFilter.includes(AssetStatus.Active);
    setShowOutdatedWarning(!isShowingFresh);
  }, [stableAssetFilters, clusterByEntity, setShowOutdatedWarning]);

  // Handler to apply the 'Active' filter and hide the warning
  const handleShowFreshAssets = useCallback(() => {
    setAssetFilters((prevFilters: Filters) => ({
      ...prevFilters,
      advancedFilters: {
        ...(prevFilters.advancedFilters || {}),
        status: [AssetStatus.Active],
      },
    }));
    setShowOutdatedWarning(false); // Hide warning immediately
  }, [setAssetFilters, setShowOutdatedWarning]);

  // Check if we have no data after filtering
  const hasNoData = !isLoading && !isLoadingRelatedData && assets.length === 0;
  const hasActiveFilters =
    filters && Object.keys(filters?.advancedFilters || {}).length > 0;

  return (
    <>
      {/* Show loading indicator when initially loading assets */}
      {isLoading && (
        <div className="bg-chariot-background/80 absolute inset-0 z-20 flex items-center justify-center backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3">
            <Loader />
            <p className="text-chariot-text-secondary text-sm">
              Loading graph data...
            </p>
          </div>
        </div>
      )}

      {/* Show empty state when filters result in no data */}
      {hasNoData && hasActiveFilters && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
          <div className="bg-chariot-background-secondary pointer-events-auto flex flex-col items-center gap-3 rounded-lg p-8 shadow-md">
            <svg
              className="text-chariot-text-secondary h-16 w-16"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M12 20.5a8.5 8.5 0 100-17 8.5 8.5 0 000 17z"
              />
            </svg>
            <p className="text-chariot-text-primary text-lg font-medium">
              No {clusterByEntity === ResourceType.Seed ? 'seeds' : 'assets'}{' '}
              match your current filters
            </p>
            <p className="text-chariot-text-secondary text-sm">
              Try adjusting your filters to see results
            </p>
          </div>
        </div>
      )}

      <ClusterCanvas
        clusters={clusters}
        graph={graphRef}
        hasSelection={false}
        currentTheme={currentTheme}
        selectedClusterValue={selectedClusterValue}
      />
      <ClusterWeightControls
        attributes={DefaultVisibleAttributesForCluster}
        clusters={clusters}
        filters={filters}
        setFilters={setFilters}
      />
      <NodeDataTable
        selectedNodes={selectedNodes}
        graph={graphRef}
        assets={routableAssets} // Use filtered routable assets
        risks={risks}
        technologies={technologies}
        seeds={seeds}
        // clusterByEntity={clusterByEntity}
      />
      {/* Conditionally render the warning banner if the asset status filter is not set to Active */}
      {showOutdatedWarning && (
        <div className="border-chariot-stroke bg-chariot-background-secondary absolute top-4 left-1/2 z-30 -translate-x-1/2 rounded-lg border p-3 shadow-md">
          <div className="flex items-center gap-3">
            <ClockAlert className="text-chariot-text-warning size-5 shrink-0" />
            <p className="text-chariot-text-warning text-sm">
              Some assets shown may be outdated or inactive. View only fresh
              assets?
            </p>
            <Button
              onClick={() => {}} // Remove the setShowOutdatedWarning call
              styleType="tertiary"
              size="sm"
              label="No"
              className="text-chariot-text-warning hover:text-chariot-text-warning-hover ml-2 whitespace-nowrap" // Added text color classes
            />
            <Button
              onClick={handleShowFreshAssets}
              styleType="primary"
              size="sm"
              label="Yes"
              className="text-chariot-text-warning hover:text-chariot-text-warning-hover ml-2 whitespace-nowrap" // Added text color classes
            />
          </div>
        </div>
      )}

      {/* Show indicator when clustering by AS fields */}
      {clusterByEntity === ResourceType.Asset &&
        currentWeights.some(
          w =>
            w.weight === 20 &&
            ['asname', 'asnumber', 'asrange'].includes(w.name)
        ) && (
          <div className="border-chariot-stroke bg-chariot-background-secondary absolute right-2 bottom-2 z-20 rounded-lg border p-3 shadow-md">
            <div className="flex items-center gap-2">
              <svg
                className="text-chariot-text-secondary size-4 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-chariot-text-secondary text-sm">
                Only internet-routable assets with AS assignments are shown
              </p>
            </div>
          </div>
        )}

      {/* Development: Viewport culling disabled
      {process.env.NODE_ENV === 'development' && shouldEnableCulling && (
        <div className="border-chariot-stroke bg-chariot-background-secondary absolute bottom-2 left-2 z-20 rounded-lg border p-3 shadow-md">
          <div className="flex flex-col gap-1">
            <p className="text-chariot-text-primary text-xs font-semibold">
              Viewport Culling Active
            </p>
            <p className="text-chariot-text-secondary text-xs">
              Visible: {cullingStats.visible} / {cullingStats.total} (
              {cullingStats.percentage.toFixed(1)}%)
            </p>
            <p className="text-chariot-text-secondary text-xs">
              Culled: {cullingStats.culled} nodes
            </p>
          </div>
        </div>
      )}
      */}
    </>
  );
};
